import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import parseISO from 'date-fns/parseISO';
import { fr, enUS, de, es } from 'date-fns/locale';
import { EN_US, FR_FR, DE_DE, ES_ES } from './constants/LanguageRegionCode';

const localeMap = {};
localeMap[EN_US] = enUS;
localeMap[FR_FR] = fr;
localeMap[DE_DE] = de;
localeMap[ES_ES] = es;

const monthDayYear = (date, locale = EN_US, longMonth = true) => {
  return new Date(date).toLocaleDateString(locale, {
    month: longMonth ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formattedDate = (date, timezoneId, languageRegionCode = EN_US) => {
  if (timezoneId) {
    return date.toLocaleDateString(languageRegionCode, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezoneId,
      timeZoneName: 'short'
    });
  }
  return date.toLocaleDateString(languageRegionCode, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const dateString = (date = new Date(), timezone = null, languageRegionCode = EN_US) => {
  return timezone
    ? new Date(date).toLocaleDateString(languageRegionCode, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        timeZoneName: 'short'
      })
    : new Date(date).toLocaleDateString(languageRegionCode, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      });
};

const daysAgoInSeconds = days => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d / 1000; // ms -> s
};

const localizedFormat = (abbreviate = false, locale = EN_US, withTime = false) => {
  let result;
  switch (locale) {
    case EN_US:
      result = abbreviate ? 'MMM. dd, yyyy' : 'MMMM dd, yyyy';
      break;
    case DE_DE:
      result = abbreviate ? 'dd. MMM. yyyy' : 'dd. MMMM yyyy';
      break;
    case FR_FR:
      result = abbreviate ? 'dd MMM yyyy' : 'dd MMMM yyyy';
      break;
    case ES_ES:
      result = abbreviate ? "dd 'de' MMM. 'de' yyyy" : "dd 'de' MMMM 'de' yyyy";
      break;
    default:
      result = abbreviate ? 'MMM dd, yyyy' : 'MMMM dd, yyyy';
  }
  if (withTime) {
    switch (locale) {
      case EN_US:
        result = `${result} hh:mm a z`;
        break;
      default:
        result = `${result} HH:mm`;
    }
  }
  return result;
};

const isToday = potentialDate => {
  const date = new Date(potentialDate);
  const today = new Date();

  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() && today.getFullYear();
};

const timeAgo = (dateStr, languageRegionCode) => {
  const date = parseISO(dateStr);
  const options = { addSuffix: true, locale: localeMap[languageRegionCode] };
  return formatDistanceToNow(date, options);
};

const parseTimeString = time => {
  return parseInt(time.split(':')[0]);
};

const betweenHours = (start, end) => {
  const [startHour, endHour] = [parseTimeString(start), parseTimeString(end)];
  const currentHour = new Date().getHours();

  // this would be that the time goes from late PM to early AM
  if (startHour >= endHour) {
    return currentHour >= startHour;
  }

  return currentHour >= startHour && currentHour <= endHour;
};

// These allow a user to edit a UTC date/time without it being automatically shown as local time by DatePicker
const adjustToSameNominalTimeForUser = utcDateTimeString => {
  return Date.parse(utcDateTimeString) + 60000 * (new Date().getTimezoneOffset());
};
const adjustFromSameNominalTimeForUser = localDateObject => {
  return new Date(localDateObject.getTime() - 60000 * (new Date().getTimezoneOffset()));
};

export { localeMap, formattedDate, monthDayYear, daysAgoInSeconds, dateString, localizedFormat, isToday, timeAgo, betweenHours, adjustToSameNominalTimeForUser, adjustFromSameNominalTimeForUser };
